import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Play, 
  Clock, 
  Calendar,
  BookOpen,
  CheckCircle2,
  ArrowRight,
  Video,
  RotateCcw
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const MyCourses = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [progressMap, setProgressMap] = useState({});

  useEffect(() => {
    fetchPurchasedCourses();
  }, []);

  const fetchPurchasedCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/user/purchased-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCourses(response.data);
      
      // Fetch progress for each course
      const progressPromises = response.data.map(course => 
        axios.get(`${API}/user/watch-progress/${course.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => ({ id: course.id, progress: res.data }))
          .catch(() => ({ id: course.id, progress: null }))
      );
      
      const progressResults = await Promise.all(progressPromises);
      const progressObj = {};
      progressResults.forEach(item => {
        if (item.progress) {
          progressObj[item.id] = item.progress;
        }
      });
      setProgressMap(progressObj);
      
    } catch (error) {
      console.error('Error fetching courses:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Kurslar yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      pilates: 'Pilates',
      yoga: 'Yoga',
      meditation: 'Meditasyon',
      fitness: 'Fitness',
      breathing: 'Nefes',
      mobility: 'Mobilite'
    };
    return labels[category] || category;
  };

  const getLevelLabel = (level) => {
    const labels = {
      beginner: 'Başlangıç',
      intermediate: 'Orta',
      advanced: 'İleri'
    };
    return labels[level] || level;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleWatchCourse = (courseId) => {
    navigate(`/watch/${courseId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Kurslarım</h1>
          <p className="text-gray-600">Satın aldığınız tüm kurslar burada</p>
        </div>

        {courses.length === 0 ? (
          /* Empty State */
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz kurs satın almadınız</h3>
              <p className="text-gray-500 mb-6">
                Kursları keşfedin ve öğrenmeye hemen başlayın!
              </p>
              <Button className="btn-primary" onClick={() => navigate('/lessons')}>
                Kursları Keşfet
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Courses Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Card 
                key={course.id} 
                className="overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-purple-400 to-purple-600">
                  {course.thumbnail_url ? (
                    <img 
                      src={`${API}${course.thumbnail_url}`}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="h-12 w-12 text-white opacity-70" />
                    </div>
                  )}
                  
                  {/* Purchased Badge */}
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Satın Alındı
                    </Badge>
                  </div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-3 left-3">
                    <Badge variant="secondary" className="bg-white/90">
                      {getCategoryLabel(course.category)}
                    </Badge>
                  </div>
                  
                  {/* Play Overlay */}
                  <div 
                    className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => handleWatchCourse(course.id)}
                  >
                    <div className="bg-white rounded-full p-4 shadow-lg">
                      <Play className="h-8 w-8 text-purple-600" fill="currentColor" />
                    </div>
                  </div>
                </div>

                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{getLevelLabel(course.level)}</Badge>
                    <span className="text-sm text-gray-500 flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {course.duration} dk
                    </span>
                  </div>
                  <CardTitle className="text-lg line-clamp-1">{course.title}</CardTitle>
                  {course.subtitle && (
                    <p className="text-sm text-gray-500">{course.subtitle}</p>
                  )}
                </CardHeader>

                <CardContent>
                  {/* Instructor */}
                  {course.instructor && (
                    <div className="flex items-center mb-4">
                      <Avatar className="h-8 w-8 mr-2">
                        <AvatarFallback className="bg-purple-100 text-purple-700 text-sm">
                          {course.instructor.full_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-gray-600">{course.instructor.full_name}</span>
                    </div>
                  )}

                  {/* Purchase Date */}
                  {course.purchase_date && (
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4 mr-1" />
                      Satın alındı: {formatDate(course.purchase_date)}
                    </div>
                  )}

                  {/* Progress Bar */}
                  {progressMap[course.id] && (
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>İlerleme</span>
                        <span>{Math.round(progressMap[course.id].progress_percent || 0)}%</span>
                      </div>
                      <Progress 
                        value={progressMap[course.id].progress_percent || 0} 
                        className="h-2"
                      />
                      {progressMap[course.id].completed && (
                        <div className="flex items-center mt-1 text-green-600 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Tamamlandı
                        </div>
                      )}
                    </div>
                  )}

                  {/* Watch Button - Dynamic text based on progress */}
                  <Button 
                    className="w-full btn-primary"
                    onClick={() => handleWatchCourse(course.id)}
                  >
                    {progressMap[course.id]?.progress_percent > 0 && !progressMap[course.id]?.completed ? (
                      <>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        İzlemeye Devam Et
                      </>
                    ) : progressMap[course.id]?.completed ? (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Tekrar İzle
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Kursu İzle
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
